import Constrants from "../constrants/constrant";

// Generic GET helper
const fetchData = async (endpoint, options = {}) => {
  try {
    const response = await Constrants.get(endpoint);
    const { isArray = false, filterOutId = null } = options;

    let data = response.data;

    if (isArray && !Array.isArray(data)) return [];
    if (filterOutId && Array.isArray(data)) data = data.filter(item => item.id !== filterOutId);

    return data;
  } catch (error) {
    console.error(`Error fetching from ${endpoint}:`, error);
    if (options?.returnEmptyArray) return [];
    throw error;
  }
};

// App Section
export const fetchRecommendedAppsHome = () => fetchData("/home-recommended", { isArray: true });
export const fetchTopAppsHome = () => fetchData("/home-top-apps", { isArray: true });
export const fetchLatestAppsHome = () => fetchData("/home-latest", { isArray: true });
export const fetchHomeAppCollections = () => fetchData("/collections", { isArray: true });
export const fetchSingleAppDetails = (slug) => fetchData(`/app/${slug}`).then(d => d.data);
export const fetchRelatedApps = (categoryId, currentAppId) =>
  fetchData(`/apps?category=${categoryId}`, { returnEmptyArray: true }).then(d =>
    Array.isArray(d.data) ? d.data.filter(app => app.id !== currentAppId) : []
  );

// Collections & Developer
export const fetchSingleCollections = (slug) => fetchData(`/collection/${slug}`);
export const fetchDeveloperBySlug = (slug) => fetchData(`/developer/${slug}`);

// Blog Section
export const fetchBlogHomeCategories = () => fetchData("/blogs/categories", { isArray: true });
export const fetchAllBlogPosts = () => fetchData("/blogs", { isArray: true });
export const fetchFeaturedBlogPosts = () => fetchData("/blogs/featured", { isArray: true });
export const fetchPopularBlogPosts = () => fetchData("/blogs/popular", { isArray: true });
export const fetchSingleBlogPost = (slug) => fetchData(`/blogs/${slug}`);
export const fetchAuthorBlogs = (authorId) => fetchData(`/blogs/author/${authorId}`);
export const fetchSinghCategoryBlogs = (slug) => fetchData(`/blogs/category/${slug}`);
export const fetchBlogsByTag = (slug) => fetchData(`/blogs/tag/${slug}`);
export const fetchRelatedBlogPosts = (categoryId, currentPostId) =>
  fetchData(`/blogs?category=${categoryId}`, { returnEmptyArray: true }).then(posts =>
    Array.isArray(posts) ? posts.filter(p => p.id !== currentPostId) : []
  );
export const fetchRecommendedApps = () => fetchData("/apps/recommended", { isArray: true });
export const fetchAppsCollections = () => fetchData("/apps/collections", { isArray: true });
export const fetchTopApps = () => fetchData("/apps/top-apps", { isArray: true });
export const fetchLatestApps = () => fetchData("/apps/latest", { isArray: true });
export const fetchPrivacyPolicy = () => fetchData("/privacy-policy");

// Faqs section
export const fetchFAQs = () => fetchData("/faqs", { isArray: true });
export const fetchFAQCategories = () => fetchData("/faqs/categories", { isArray: true });
export const fetchFAQsByCategory = (category) => fetchData(`/faqs/category/${category}`, { isArray: true });
export const searchFAQs = (query) => fetchData(`/faqs/search?q=${encodeURIComponent(query)}`, { isArray: true });
export const fetchFAQStats = () => fetchData("/faqs/stats");

// Help Center Section
export const fetchHelpCenterData = () => fetchData("/help-center");
export const fetchHelpCategories = () => fetchData("/help/categories", { isArray: true });
export const fetchHelpCategoryBySlug = (slug) => fetchData(`/help/category/${slug}`);
export const fetchHelpArticleBySlug = (slug) => fetchData(`/help/article/${slug}`);
export const fetchHelpArticlesByCategory = (categorySlug) => fetchData(`/help/category/${categorySlug}/articles`, { isArray: true });
export const searchHelpArticles = (query) => fetchData(`/help/search?q=${encodeURIComponent(query)}`);
export const fetchPopularHelpArticles = () => fetchData("/help/popular", { isArray: true });
export const fetchRelatedHelpArticles = (articleId) => fetchData(`/help/article/${articleId}/related`, { isArray: true });

// Contact/Support Ticket
export const submitHelpTicket = async (ticketData) => {
  try {
    const response = await Constrants.post("/support/ticket", ticketData);
    return response.data;
  } catch (error) {
    console.error("Support ticket error:", error.response?.data || error.message);
    throw error;
  }
};

// Article Feedback
export const submitArticleFeedback = async (articleId, feedbackData) => {
  try {
    const response = await Constrants.post(`/help/article/${articleId}/feedback`, feedbackData);
    return response.data;
  } catch (error) {
    console.error("Feedback error:", error.response?.data || error.message);
    throw error;
  }
};

// Admin Help Center Functions (require authentication)
export const createHelpArticle = async (articleData) => {
  const token = localStorage.getItem("token");
  return Constrants.post("/admin/help/articles", articleData, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const updateHelpArticle = async (id, articleData) => {
  const token = localStorage.getItem("token");
  return Constrants.put(`/admin/help/articles/${id}`, articleData, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const deleteHelpArticle = async (id) => {
  const token = localStorage.getItem("token");
  return Constrants.delete(`/admin/help/articles/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const createHelpCategory = async (categoryData) => {
  const token = localStorage.getItem("token");
  return Constrants.post("/admin/help/categories", categoryData, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const updateHelpCategory = async (id, categoryData) => {
  const token = localStorage.getItem("token");
  return Constrants.put(`/admin/help/categories/${id}`, categoryData, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const deleteHelpCategory = async (id) => {
  const token = localStorage.getItem("token");
  return Constrants.delete(`/admin/help/categories/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

// Admin FAQ Functions (require authentication)
export const createFAQ = async (faqData) => {
  const token = localStorage.getItem("token");
  return Constrants.post("/admin/faqs", faqData, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const updateFAQ = async (id, faqData) => {
  const token = localStorage.getItem("token");
  return Constrants.put(`/admin/faqs/${id}`, faqData, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const deleteFAQ = async (id) => {
  const token = localStorage.getItem("token");
  return Constrants.delete(`/admin/faqs/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const toggleFAQStatus = async (id) => {
  const token = localStorage.getItem("token");
  return Constrants.patch(`/admin/faqs/${id}/toggle`, {}, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const trackAppDownload = (appId) => {
  const token = localStorage.getItem("token");
  const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  
  return Constrants.post(`/apps/${appId}/download`, {}, config)
    .catch(error => {
      console.error("Download tracking error:", error.response?.data);
      throw error;
    });
};

export const subscribeToNewsletter = async (name, email) => {
  try {
    const response = await Constrants.post("/subscribe", { name, email });
    return response.data;
  } catch (error) {
    console.error("Subscription error:", error.response?.data || error.message);
    throw error;
  }
};


// Search functions
export const fetchSearchSuggestions = (query) => 
  fetchData(`/search/suggestions?q=${encodeURIComponent(query)}`, { returnEmptyArray: true })
    .then(d => d.data || []);

export const fetchSearchResults = (query) => 
  fetchData(`/search?q=${encodeURIComponent(query)}`)
    .then(d => d.data || { apps: [], games: [], blogs: [] });

// Contact Section
export const submitContactForm = async (formData) => {
  try {
    const response = await Constrants.post("/contact", formData);
    return response;
  } catch (error) {
    console.error("Contact submission error:", error.response?.data);
    throw error;
  }
};

export const fetchContactHistory = () => {
  const token = localStorage.getItem("token");
  return Constrants.get("/contact/history", {
    headers: { Authorization: `Bearer ${token}` }
  });
};

// Admin Contact Functions
export const fetchAllContacts = (params = {}) => {
  const token = localStorage.getItem("token");
  return Constrants.get("/admin/contacts", {
    params,
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const fetchContactStats = () => {
  const token = localStorage.getItem("token");
  return Constrants.get("/admin/contacts/stats", {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const fetchSingleContact = (id) => {
  const token = localStorage.getItem("token");
  return Constrants.get(`/admin/contacts/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};

export const updateContactStatus = (id, status) => {
  const token = localStorage.getItem("token");
  return Constrants.patch(`/admin/contacts/${id}/status`, 
    { status },
    { headers: { Authorization: `Bearer ${token}` }}
  );
};

export const updateContactNotes = (id, notes) => {
  const token = localStorage.getItem("token");
  return Constrants.patch(`/admin/contacts/${id}/notes`, 
    { notes },
    { headers: { Authorization: `Bearer ${token}` }}
  );
};

export const toggleContactRead = (id) => {
  const token = localStorage.getItem("token");
  return Constrants.patch(`/admin/contacts/${id}/read`, {},
    { headers: { Authorization: `Bearer ${token}` }}
  );
};

export const deleteContact = (id) => {
  const token = localStorage.getItem("token");
  return Constrants.delete(`/admin/contacts/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
};